import { GradientButton } from "@/components/common";
import { Link } from "react-router";
import { useNavigate } from 'react-router-dom';
import { Ticket, FileText, Info } from "lucide-react";

const ContactCard = ({ icon: Icon, title, description, to }) => (
	<Link to={to} className="block group">
		<div className="relative h-full backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 bg-gradient-to-br from-gray-900/30 to-gray-800/20 transition-all duration-300 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1 overflow-hidden">
			{/* Subtle gradient overlay on hover */}
			<div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/5 group-hover:to-purple-500/5 transition-all duration-300 rounded-2xl" />
			
			<div className="relative flex flex-col items-center text-center space-y-4">
				{/* Icon container */}
				<div className="relative">
					<div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-lg group-hover:bg-blue-500/30 transition-all duration-300" />
					<div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-all duration-300 shadow-lg shadow-blue-500/30">
						<Icon className="w-8 h-8 text-white" />
					</div>
				</div>

				{/* Text content */}
				<div className="space-y-2">
					<h3 className="text-white font-semibold text-lg">
						{title}
					</h3>
					<p className="text-gray-300 text-sm leading-relaxed">
						{description}
					</p>
				</div>

				{/* Arrow indicator */}
				<div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
					<svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
					</svg>
				</div>
			</div>
		</div>
	</Link>
);

export default function ContactPage() {
	const navigate = useNavigate();

	const handleContactClick = () => {
		navigate('/contact-us');
	};

	return (
		<div className="relative min-h-screen overflow-hidden">
			{/* Subtle background glow effects */}
			<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
			<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl"></div>

			{/* Accent lines */}
			<div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>

			<div className="relative z-10">
				{/* Hero Section */}
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
					<div className="max-w-6xl mx-auto">
						<div className="text-center mb-16 space-y-6">
							{/* Badge */}
							<div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm">
								<div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse mr-2" />
								<span className="text-blue-300 text-sm font-medium">We're Here to Help</span>
							</div>

							{/* Main heading */}
							<h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
								What's on your{" "}
								<span className="bg-gradient-to-r from-blue-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
									mind?
								</span>
							</h2>

							{/* Subheading */}
							<p className="text-gray-300 md:text-lg max-w-2xl mx-auto leading-relaxed">
								We're ready to assist you! Let us know what you need, and we'll
								connect you with the right experts to help you succeed.
							</p>
						</div>

						{/* Cards Grid */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
							<ContactCard
								icon={Ticket}
								title="Log a Ticket"
								description="Report an issue or request technical support from our expert team"
								to="/log-ticket"
							/>

							<ContactCard
								icon={FileText}
								title="Request a Service"
								description="Get started with our professional services tailored to your needs"
								to="/request-for-services"
							/>
						</div>

						{/* Additional Info Section */}
						<div className="max-w-4xl mx-auto">
							<div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-gray-700/50">
								<div className="flex flex-col sm:flex-row items-center justify-between gap-6">
									<div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
										<div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-blue-500/30">
											<Info className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
										</div>
										<div>
											<h3 className="text-white font-semibold text-lg sm:text-xl mb-1">Need immediate assistance?</h3>
											<p className="text-gray-300 text-sm">Our support team is available 24/7</p>
										</div>
									</div>
									<div className="w-full sm:w-auto">
										<GradientButton onClick={handleContactClick} className="w-full sm:w-auto">
											Contact Us
										</GradientButton>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}